import React, { useCallback, useState } from 'react';
import type { ContentItem } from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import StyledModal from '../../styles/StyledModal';
import type { DocumentPickTarget } from './editorSidebarContext';
import { useCropCanvas } from './cropAdjustBoxes';
import { nextBatchStart, type ImportedPageRange } from './documentImportLimits';
import type { Box as CropBox } from './cropAdjustBoxes';
import DocumentViewerDialogBody, {
  ViewerActions,
  viewerSubtitle,
} from './DocumentViewerDialogBody';
import { importLibrarySelection, useDocumentLibraryImport } from './useDocumentLibraryImport';
import type { ImageCardsCropMeta } from './useImageCardsSplit';

export interface DocumentViewerDialogProps {
  open: boolean;
  documentAsset: DocumentAsset | null;
  pickTarget: DocumentPickTarget | null;
  modelType: string;
  modelId: string;
  backendService: BackendService;
  onClose: () => void;
  onImported?: (
    sections: ContentItem[],
    cropMeta?: ImageCardsCropMeta | null,
  ) => void | Promise<void>;
  autoAdvance?: boolean;
  /** The last batch already imported from this document; the viewer opens on the next one. */
  resumeAfter?: ImportedPageRange | null;
  /** A page-limited batch was imported (pages may remain). */
  onBatchImported?: (range: ImportedPageRange) => void;
  /**
   * The import is complete — a page or region, or the document's last batch. Defaults to
   * `onClose`; `onClose` itself is for cancelling, or stopping with batches still to import.
   */
  onFinished?: () => void;
}

const DEFAULT_BOX: CropBox = { left: 50, top: 50, width: 300, height: 200 };

function useDocumentViewerState(initialPage: number, resumeAfter: ImportedPageRange | null) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [imageDims, setImageDims] = useState<{ width: number; height: number } | null>(null);
  const [mode, setMode] = useState<'image' | 'structure'>('structure');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [lastImportedRange, setLastImportedRange] = useState<ImportedPageRange | null>(resumeAfter);
  const source = imageDims ? { url: '', width: imageDims.width, height: imageDims.height } : null;
  const crop = useCropCanvas(source, [DEFAULT_BOX]);
  return {
    currentPage,
    setCurrentPage,
    imageDims,
    setImageDims,
    mode,
    setMode,
    confirming,
    setConfirming,
    confirmError,
    setConfirmError,
    lastImportedRange,
    setLastImportedRange,
    ...crop,
  };
}

interface ConfirmCallbacks {
  onImported?: DocumentViewerDialogProps['onImported'];
  onBatchImported?: (range: ImportedPageRange) => void;
  onFinished: () => void;
  autoAdvance: boolean;
}

type ViewerState = ReturnType<typeof useDocumentViewerState>;
type LibraryImportState = ReturnType<typeof useDocumentLibraryImport>;

/**
 * After a successful import: a page-limited batch with pages left selects the next batch and
 * stays open; anything else (a page, a region, or the last batch) is finished.
 */
function advanceOrFinish(
  importedRange: ImportedPageRange,
  v: ViewerState,
  libraryImport: LibraryImportState,
  { onBatchImported, onFinished, autoAdvance }: ConfirmCallbacks,
) {
  const batched =
    autoAdvance && (libraryImport.scopeMode === 'document' || libraryImport.scopeMode === 'range');
  if (batched) onBatchImported?.(importedRange);
  if (batched && libraryImport.selectNextBatch(importedRange.toPage, v.setCurrentPage)) {
    v.setLastImportedRange(importedRange);
    v.setImageDims(null);
  } else {
    onFinished();
  }
}

async function confirmLibraryImport(
  documentAsset: DocumentAsset,
  v: ViewerState,
  libraryImport: LibraryImportState,
  target: { backendService: BackendService; modelType: string; modelId: string },
  callbacks: ConfirmCallbacks,
) {
  const { onImported } = callbacks;
  if (!onImported) return;
  v.setConfirming(true);
  v.setConfirmError(null);
  try {
    const importedRange = await importLibrarySelection({
      documentAsset,
      ...target,
      scopeMode: libraryImport.scopeMode,
      currentPage: v.currentPage,
      fromPage: libraryImport.fromPage,
      toPage: libraryImport.toPage,
      pageCount: documentAsset.pageCount ?? 0,
      box: v.boxes[0] ?? DEFAULT_BOX,
      onImported,
    });
    advanceOrFinish(importedRange, v, libraryImport, callbacks);
  } catch (err) {
    v.setConfirmError((err as Error)?.message ?? 'Something went wrong.');
  } finally {
    v.setConfirming(false);
  }
}

/** The viewer's page, crop and scope state, resumed at the next batch when there is one. */
function useDocumentViewer(props: DocumentViewerDialogProps) {
  const { documentAsset, backendService, modelType, modelId, autoAdvance = false } = props;
  const pageCount = documentAsset?.pageCount ?? 0;
  const resumeAfter = props.resumeAfter ?? null;
  const resume = autoAdvance && nextBatchStart(resumeAfter, pageCount) ? resumeAfter : null;
  const v = useDocumentViewerState(nextBatchStart(resume, pageCount) ?? 1, resume);
  const libraryImport = useDocumentLibraryImport(pageCount, resume);

  const handleConfirm = () => {
    if (!documentAsset) return;
    void confirmLibraryImport(
      documentAsset,
      v,
      libraryImport,
      { backendService, modelType, modelId },
      {
        onImported: props.onImported,
        onBatchImported: props.onBatchImported,
        onFinished: props.onFinished ?? props.onClose,
        autoAdvance,
      },
    );
  };

  const { setImageDims, measure } = v;
  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setImageDims({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight });
      measure();
    },
    [setImageDims, measure],
  );

  const handleSelectPage = (page: number) => {
    libraryImport.selectPage(page, v.setCurrentPage);
    setImageDims(null);
  };

  return { v, libraryImport, handleConfirm, handleImageLoad, handleSelectPage };
}

export default function DocumentViewerDialog(props: DocumentViewerDialogProps) {
  const { open, documentAsset, pickTarget, backendService, onClose, onImported } = props;
  const { v, libraryImport, handleConfirm, handleImageLoad, handleSelectPage } =
    useDocumentViewer(props);
  const isLibraryImport = !!onImported;

  if (!documentAsset) return null;

  const pageReady = v.imageDims !== null;

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      title={documentAsset.title}
      subtitle={viewerSubtitle(pickTarget)}
      align="left"
      maxWidth="lg"
      fullWidth
      actions={
        <ViewerActions
          confirming={v.confirming}
          hasImported={v.lastImportedRange !== null}
          pageReady={pageReady}
          pickTarget={pickTarget}
          libraryImport={isLibraryImport ? libraryImport : null}
          mode={v.mode}
          onClose={onClose}
          onConfirm={handleConfirm}
        />
      }
    >
      <DocumentViewerDialogBody
        documentAsset={documentAsset}
        backendService={backendService}
        currentPage={v.currentPage}
        pageCount={documentAsset.pageCount ?? 0}
        pageUrl={backendService.getDocumentPageUrl(documentAsset.externalId, v.currentPage)}
        mode={v.mode}
        confirmError={v.confirmError}
        lastImportedRange={v.lastImportedRange}
        wrapRef={v.wrapRef}
        imageDims={v.imageDims}
        boxes={v.boxes}
        scale={v.scale}
        guides={v.guides}
        begin={v.begin}
        libraryImport={isLibraryImport ? libraryImport : null}
        onSelectPage={handleSelectPage}
        onModeChange={v.setMode}
        onImageLoad={handleImageLoad}
      />
    </StyledModal>
  );
}
